import { addDoc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, getConverter } from '../../../firebase';
import { createAppAsyncThunk } from '../../../hooks';
import { SelectType, namedOperations } from 'gql/graphql';
import getStytchOrg from '../../../util/getStytchOrgId';
import type { CompData, CompSubString, CompTakeData } from '../../RecordingSessionDataGrid/types';
import { episodeCompByIdRef, episodeCompsRef, episodeSelectInfoRef } from '../util';
import { SelectInfoItem } from '../types';
import { selectComps, selectSelectInfos, selectTakeGroups, selectLineRevisionChains } from './selectors';
import { gql } from '@apollo/client';

type addCompParams = {
    sessionId: string
    episodeId: string
    lineIds: string[]
    takeId: string
    subString: CompSubString
    /** Which line the selected text came from */
    lineId?: string
    /** Position index of the selected line in lineIds */
    lineIndex?: number
}

export const addComp = createAppAsyncThunk(
    'recordingSession/comps/add',
    async ({ sessionId, episodeId, lineIds, takeId, subString, lineId, lineIndex }: addCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompsRef(sessionId, episodeId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const allComps = selectComps(state);
        const compNo = allComps.length + 1;

        const newComp = await addDoc(compRef, {
            takes: [{
                takeId,
                subStrings: [subString],
                ...(lineId ? { lineId } : {}),
                ...(lineIndex !== undefined ? { lineIndex } : {}),
            }],
            note: '',
            lineIds,
            selectType: SelectType.None,
            compNo,
        });

        return newComp.id;
    }
)

type removeCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const removeComp = createAppAsyncThunk(
    'recordingSession/comps/remove',
    async ({ sessionId, episodeId, compId }: removeCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        // Get all comps, sort by compNo, and renumber after deletion
        const state = thunkAPI.getState();
        const allComps = selectComps(state).filter(c => c.id !== compId);

        const batch = writeBatch(db);
        batch.delete(compRef);

        // Renumber remaining comps
        const sorted = [...allComps].sort((a, b) => a.compNo - b.compNo);
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i].compNo !== i + 1) {
                const ref = episodeCompByIdRef(sessionId, episodeId, sorted[i].id!).withConverter(compConverter);
                batch.update(ref, 'compNo', i + 1);
            }
        }

        await batch.commit();
    }
);

type addCompTakeParams = {
    sessionId: string
    episodeId: string
    compId: string
    takeId: string
    subString: CompSubString
    /** Which line the selected text came from */
    lineId?: string
    /** Position index of the selected line in lineIds */
    lineIndex?: number
    /** Current full set of positions (from the caller's local state), kept in sync with comp.lineIds */
    lineIds?: string[]
}

export const addCompTake = createAppAsyncThunk(
    'recordingSession/comps/addTake',
    async ({ sessionId, episodeId, compId, takeId, subString, lineId, lineIndex, lineIds }: addCompTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const existingTake = comp.takes.find(t => t.takeId === takeId);
        if (existingTake) {
            const newSubStrings = [...existingTake.subStrings, subString];

            const newTakes = comp.takes.map(t => {
                if (t.takeId === takeId) {
                    return { ...t, subStrings: newSubStrings };
                }
                return t;
            });

            // lineIds is passed by the caller (its authoritative current position list) so a
            // new position can be persisted here too, not just its take
            await updateDoc(compRef, { takes: newTakes, ...(lineIds ? { lineIds } : {}) });
        } else {
            const newTake: CompTakeData = {
                takeId,
                subStrings: [subString],
                ...(lineId ? { lineId } : {}),
                ...(lineIndex !== undefined ? { lineIndex } : {}),
            };
            const newTakes = [...comp.takes, newTake];
            // lineIds is passed by the caller (its authoritative current position list) so a
            // new position can be persisted here too, not just its take
            await updateDoc(compRef, { takes: newTakes, ...(lineIds ? { lineIds } : {}) });
        }
    }
)

/**
 * Removes the given takeIds from a comp's `takes`, and drops any lineId from
 * `lineIds` that no longer has a remaining take backing it (a comp should only
 * ever reference lines that currently have takes in it).
 * Returns `null` when the comp has no takes/lines left and should be deleted.
 */
export function pruneCompAfterTakeRemoval(
    comp: CompData,
    removedTakeIds: Set<string>
): { takes: CompTakeData[]; lineIds: string[] } | null {
    const remainingTakes = comp.takes.filter(t => !removedTakeIds.has(t.takeId));
    if (remainingTakes.length === 0) return null;

    const removedLineIds = comp.takes
        .filter(t => removedTakeIds.has(t.takeId) && t.lineId)
        .map(t => t.lineId!);
    const newLineIds = removedLineIds.length === 0
        ? comp.lineIds
        : comp.lineIds.filter(id => !removedLineIds.includes(id) || remainingTakes.some(t => t.lineId === id));

    if (newLineIds.length === 0) return null;

    return { takes: remainingTakes, lineIds: newLineIds };
}

type removeCompTakeParams = {
    sessionId: string
    episodeId: string
    compId: string
    takeId: string
}

export const removeCompTake = createAppAsyncThunk(
    'recordingSession/comps/removeTake',
    async ({ sessionId, episodeId, compId, takeId }: removeCompTakeParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const result = pruneCompAfterTakeRemoval(comp, new Set([takeId]));
        if (result === null) {
            await updateDoc(compRef, { takes: []});
        } else {
            await updateDoc(compRef, result);
        }
    }
)

type circleCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const circleComp = createAppAsyncThunk(
    'recordingSession/comps/circle',
    async ({ sessionId, episodeId, compId }: circleCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);

        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        // Toggle: if already circled, deselect; otherwise circle (with cross-demotion)
        const willCircle = comp.selectType !== SelectType.Select;

        if (willCircle) {
            const batch = writeBatch(db);

            // Demote other circled comps on the same line (or revision chain)
            const allComps = selectComps(state);
            const lineRevisionChains = selectLineRevisionChains(state);
            const compRevisionChainId = lineRevisionChains[comp.lineIds[0]];
            const sameChainLineIds = compRevisionChainId
                ? new Set(Object.entries(lineRevisionChains).filter(([, rcId]) => rcId === compRevisionChainId).map(([lineId]) => lineId))
                : new Set(comp.lineIds);
            for (const other of allComps.filter(
                c => c.id !== compId && c.lineIds.some(id => sameChainLineIds.has(id)) && c.selectType === SelectType.Select
            )) {
                const otherRef = episodeCompByIdRef(sessionId, episodeId, other.id!).withConverter(compConverter);
                batch.update(otherRef, { selectType: SelectType.Alt });
            }

            // Demote the circled take for this line (if any)
            const allTakeGroups = selectTakeGroups(state);
            const allSelectInfos = selectSelectInfos(state);
            const lineSubTakeIds = new Set(
                allTakeGroups
                    .filter(tg => tg.lineIds.some(id => sameChainLineIds.has(id)))
                    .flatMap(tg => tg.subTakes.map(st => st.id!))
            );
            const selectInfoConverter = getConverter<SelectInfoItem>(org);
            for (const si of allSelectInfos.filter(si => si.circle && lineSubTakeIds.has(si.circle))) {
                const siRef = episodeSelectInfoRef(sessionId, episodeId, si.id!).withConverter(selectInfoConverter);
                batch.update(siRef, { circle: '', alt: [...(si.alt ?? []), si.circle!]});
            }

            batch.update(compRef, { selectType: SelectType.Select });
            await batch.commit();
            return;
        }

        await updateDoc(compRef, { selectType: SelectType.None });
    }
)

type altCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const altComp = createAppAsyncThunk(
    'recordingSession/comps/alt',
    async ({ sessionId, episodeId, compId }: altCompParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);
        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        // Toggle: if already alt, deselect; otherwise mark as alt
        const newType = comp.selectType !== SelectType.Alt ? SelectType.Alt : SelectType.None;
        await updateDoc(compRef, { selectType: newType });
    }
)

type reorderCompLinesParams = {
    sessionId: string
    episodeId: string
    compId: string
    fromIndex: number
    toIndex: number
}

export const reorderCompLines = createAppAsyncThunk(
    'recordingSession/comps/reorderLines',
    async ({ sessionId, episodeId, compId, fromIndex, toIndex }: reorderCompLinesParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);
        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const newLineIds = [...comp.lineIds];
        const [removed] = newLineIds.splice(fromIndex, 1);
        newLineIds.splice(toIndex, 0, removed);

        // Update takes: adjust lineIndex for the two affected positions
        const updatedTakes = comp.takes.map(take => {
            const takeLineIndex = (take as { lineIndex?: number }).lineIndex;
            if (takeLineIndex === undefined) return take;
            // If this take was at fromIndex, move it to toIndex
            if (takeLineIndex === fromIndex) {
                return { ...take, lineIndex: toIndex };
            }
            // If between fromIndex and toIndex, shift accordingly
            if (fromIndex < toIndex && takeLineIndex > fromIndex && takeLineIndex <= toIndex) {
                return { ...take, lineIndex: takeLineIndex - 1 };
            }
            if (fromIndex > toIndex && takeLineIndex >= toIndex && takeLineIndex < fromIndex) {
                return { ...take, lineIndex: takeLineIndex + 1 };
            }
            return take;
        });

        await updateDoc(compRef, { lineIds: newLineIds, takes: updatedTakes });
    }
)

type removeCompLineParams = {
    sessionId: string
    episodeId: string
    compId: string
    lineIndex: number
}

export const removeCompLine = createAppAsyncThunk(
    'recordingSession/comps/removeLine',
    async ({ sessionId, episodeId, compId, lineIndex }: removeCompLineParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);
        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        const state = thunkAPI.getState();
        const comp = selectComps(state).find(c => c.id === compId);

        if (!comp) return thunkAPI.rejectWithValue('Comp not found');

        const newLineIds = comp.lineIds.filter((_, index) => index !== lineIndex);

        // If no lines left, delete the comp
        if (newLineIds.length === 0) {
            await deleteDoc(compRef);
            return;
        }

        // Drop takes that belonged to the removed position, and shift lineIndex down
        // for takes after it, to stay in sync with the now-shorter lineIds array
        const newTakes = comp.takes
            .filter(take => {
                const takeLineIndex = (take as { lineIndex?: number }).lineIndex;
                return takeLineIndex === undefined || takeLineIndex !== lineIndex;
            })
            .map(take => {
                const takeLineIndex = (take as { lineIndex?: number }).lineIndex;
                if (takeLineIndex === undefined || takeLineIndex < lineIndex) return take;
                return { ...take, lineIndex: takeLineIndex - 1 };
            });

        await updateDoc(compRef, { lineIds: newLineIds, takes: newTakes });
    }
)

type promoteCompParams = {
    sessionId: string
    episodeId: string
    compId: string
}

export const promoteComp = createAppAsyncThunk(
    'recordingSession/comps/promote',
    async ({ sessionId, episodeId, compId }: promoteCompParams, thunkAPI) => {
        const mutation = gql`
            mutation PromoteComp($compId: ID!, $sessionId: ID!, $episodeId: ID!) {
                promoteComp(compId: $compId, sessionId: $sessionId, episodeId: $episodeId) {
                    id
                }
            }
        `;

        const result = await window.apolloClient.mutate({
            mutation,
            variables: { compId, sessionId, episodeId },
        });

        const lineId = result.data?.promoteComp?.id;

        if (!lineId) {
            return thunkAPI.rejectWithValue('Failed to get promoted line ID');
        }

        // Refetch table data to update comp display
        try {
            await window.apolloClient.refetchQueries({
                include: [
                    namedOperations.Query.GetSessionLines_TableWrapper,
                    namedOperations.Query.GetCompDetail_CompDetailWrapper,
                ],
            });
        } catch (e) {
            console.warn('Failed to refetch queries:', e);
        }

        return { lineId };
    }
)

type updateCompNameParams = {
    sessionId: string
    episodeId: string
    compId: string
    name: string
}

export const updateCompName = createAppAsyncThunk(
    'recordingSession/comps/updateName',
    async ({ sessionId, episodeId, compId, name }: updateCompNameParams, thunkAPI) => {
        const org = getStytchOrg();

        if (!org) return thunkAPI.rejectWithValue('Stytch not initialized or something else went wrong');

        const compConverter = getConverter<CompData>(org);
        const compRef = episodeCompByIdRef(sessionId, episodeId, compId).withConverter(compConverter);

        await updateDoc(compRef, { name });

        try {
            await window.apolloClient.refetchQueries({
                include: 'active',
            });
        } catch (e) {
            console.warn('Failed to refetch queries:', e);
        }

        return { compId, name };
    }
)